/**
 * The world the diorama sits in — desert, dunes, and a skyline of
 * megastructures on the far edge of it.
 *
 * ## Preview only, and that is not a limitation
 *
 * This exists **only in `preview` mode**, and the guard is load-bearing: in
 * `webxr` and `camera` the backdrop is the player's actual room, and a sixty-
 * metre sand plane would draw straight over the camera feed and destroy the
 * one thing AR is for. Desktop is the mode with nothing behind the garden, so
 * desktop is the mode that gets a world.
 *
 * ## The scale fiction
 *
 * The garden is a real object about a metre across. The desert is a painted
 * backdrop at twenty to thirty-five metres. Nothing reconciles those two, and
 * nothing should: a diorama floating in a vast landscape is the image, and
 * trying to make the plot "really" sit in a real desert would just make the
 * plot look like a coin.
 *
 * Buildings are therefore sized by the angle they subtend rather than by any
 * real height — four to nine metres tall at that distance reads as a distant
 * megastructure, where a literal 110 m tower would read as a wall.
 *
 * ## They are shapes, not claims
 *
 * A tower, a long horizontal slab, paired domes, a terraced mass, an arena
 * ring: the generic furniture of a city seen from a long way off. Nothing
 * here asserts a figure and nothing depicts a building that exists, which is
 * what makes a skyline safe to invent at all.
 *
 * It is still a *dry* horizon — sixty metres of sand behind the plot. Making
 * the backdrop follow the player's own part of the world is outstanding work;
 * see the rebrand spec in `docs/superpowers/specs/`.
 */

import * as THREE from 'three';

export interface Horizon {
  root: THREE.Group;
  /** Blend the sky and sand toward the current weather. */
  setPalette(
    skyTop: number,
    skyBottom: number,
    ground: number,
    exposure: number,
    dt: number,
  ): void;
  update(dt: number, time: number): void;
  dispose(): void;
}

const SKY_DOME_R = 46;

/**
 * Nothing in the near field is placed inside this radius, metres.
 *
 * Sized for the *largest* the plot can ever be — the wetland tier at 1.6 m
 * plus its pavilion ring — rather than for the terrace it starts as. The
 * horizon is built once, at `begin()`, and the garden opens out under it as
 * districts unlock; a keep-out measured against the starting plot puts
 * boulders inside the wetland by the time a player gets there.
 */
const KEEP_OUT = 2.45;
const SAND = 0xd6c096;
const SAND_BASE = new THREE.Color(SAND);

/**
 * Radial falloff for the sun's halo, drawn once to a canvas.
 *
 * A plain disc with low opacity reads as a flat pink plate stuck on the sky —
 * a glow is entirely its gradient. Generated rather than shipped, like the
 * particle sprite, because the whole point is that nothing here downloads.
 */
function makeGlowTexture(): THREE.Texture {
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,0.85)');
  g.addColorStop(0.18, 'rgba(255,255,255,0.42)');
  g.addColorStop(0.5, 'rgba(255,255,255,0.12)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.needsUpdate = true;
  return tex;
}

/** Deterministic PRNG, so the skyline is the same city on every load. */
function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

/**
 * @param fog The scene's fog, updated in place so the haze the far dunes fade
 *   into always matches the sky they fade into. Left on a fixed colour it
 *   pinned the whole distance to one beige regardless of weather, and a
 *   sandstorm ended up with a cream horizon.
 */
export function buildHorizon(fog: THREE.Fog): Horizon {
  const root = new THREE.Group();
  const owned: Array<{ dispose(): void }> = [];
  const track = <T extends { dispose(): void }>(x: T): T => {
    owned.push(x);
    return x;
  };
  const rand = rng(20260820);

  /* ---------------------------------------------------------------- *
   * Sky
   *
   * A vertex-coloured dome rather than a shader: the gradient has to be
   * re-tinted every time the weather turns, and lerping a colour attribute
   * is less machinery than a uniform plus a custom material to hang it on.
   * ---------------------------------------------------------------- */

  const skyGeo = track(new THREE.SphereGeometry(SKY_DOME_R, 24, 16));
  const skyColors = new Float32Array(skyGeo.attributes.position.count * 3);
  skyGeo.setAttribute('color', new THREE.BufferAttribute(skyColors, 3));
  const skyMat = track(
    new THREE.MeshBasicMaterial({
      vertexColors: true,
      side: THREE.BackSide,
      depthWrite: false,
      fog: false,
    }),
  );
  const sky = new THREE.Mesh(skyGeo, skyMat);
  // Behind everything, always. Without this the dome z-fights the far plane
  // on some mobile GPUs and flickers black.
  sky.renderOrder = -1;
  root.add(sky);

  const zenith = new THREE.Color(0x2b4a72);
  const horizonCol = new THREE.Color(0xf0c489);

  function paintSky(): void {
    const p = skyGeo.attributes.position;
    const c = skyGeo.attributes.color as THREE.BufferAttribute;
    const tmp = new THREE.Color();
    for (let i = 0; i < p.count; i++) {
      // Height up the dome, biased so most of the gradient's range is spent
      // near the horizon where the eye actually is.
      const h = THREE.MathUtils.clamp(p.getY(i) / SKY_DOME_R, -1, 1);
      const t = Math.pow(THREE.MathUtils.clamp(h * 0.5 + 0.5, 0, 1), 0.45);
      tmp.copy(horizonCol).lerp(zenith, t);
      c.setXYZ(i, tmp.r, tmp.g, tmp.b);
    }
    c.needsUpdate = true;
  }
  paintSky();

  /* ---------------------------------------------------------------- *
   * Sand
   * ---------------------------------------------------------------- */

  const sandMat = track(
    new THREE.MeshStandardMaterial({ color: SAND, roughness: 1, flatShading: true }),
  );
  const ground = new THREE.Mesh(track(new THREE.CircleGeometry(SKY_DOME_R * 0.95, 48)), sandMat);
  ground.rotation.x = -Math.PI / 2;
  // Just below zero so the plot's own shadow-catcher plane still wins the
  // depth test and its contact shadow lands on the sand.
  ground.position.y = -0.005;
  ground.receiveShadow = true;
  root.add(ground);

  // Dune ridges — long, low and shallow-angled, purely to stop the sand
  // reading as a flat disc where it meets the sky.
  /* Their own material, a shade warmer and darker than the flat. Sharing the
     ground's colour made forty dunes completely invisible — same albedo, and
     the top faces catch almost the same light as the plane they sit on. */
  const duneMat = track(
    new THREE.MeshStandardMaterial({ color: 0xc8ad82, roughness: 1, flatShading: true }),
  );
  const duneGeo = track(new THREE.ConeGeometry(1, 1, 5, 1));
  const DUNE_COUNT = 40;
  const dunes = new THREE.InstancedMesh(duneGeo, duneMat, DUNE_COUNT);
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const e = new THREE.Euler();
  for (let i = 0; i < DUNE_COUNT; i++) {
    const a = rand() * Math.PI * 2;
    // Squared distribution pushes most of them out toward the horizon while
    // still seating a few close in, so the sand has depth cues at both ends
    // instead of one ring of bumps.
    const d = 5 + Math.pow(rand(), 0.6) * 32;
    const w = 2.5 + rand() * 10;
    const h = 0.35 + rand() * 1.9;
    e.set(0, rand() * Math.PI, 0);
    q.setFromEuler(e);
    dunes.setMatrixAt(
      i,
      m.compose(
        new THREE.Vector3(Math.cos(a) * d, h * 0.12, Math.sin(a) * d),
        q,
        new THREE.Vector3(w, h, w * (0.45 + rand() * 0.5)),
      ),
    );
  }
  dunes.instanceMatrix.needsUpdate = true;
  dunes.receiveShadow = true;
  root.add(dunes);

  /* ---------------------------------------------------------------- *
   * Near-field scatter
   *
   * Rocks and scrub from the keep-out radius outward. This is the part of the
   * ground the camera actually looks at — the dunes live out past the fog and
   * the city is on the skyline, which left the whole middle distance as an
   * unbroken plane of orange taking up half the frame. Two instanced meshes
   * fix it for two draw calls.
   * ---------------------------------------------------------------- */

  const rockMat = track(
    new THREE.MeshStandardMaterial({ color: 0xa8916d, roughness: 1, flatShading: true }),
  );
  const ROCKS = 44;
  const rocks = new THREE.InstancedMesh(
    track(new THREE.DodecahedronGeometry(1, 0)),
    rockMat,
    ROCKS,
  );
  for (let i = 0; i < ROCKS; i++) {
    const a = rand() * Math.PI * 2;
    // Never inside KEEP_OUT: that is the plot's own ground, and a boulder
    // growing out of the terrace looks like a bug.
    const d = KEEP_OUT + Math.pow(rand(), 0.7) * 8;
    const r = 0.05 + rand() * 0.22;
    e.set(rand() * 3, rand() * 3, rand() * 3);
    q.setFromEuler(e);
    rocks.setMatrixAt(
      i,
      m.compose(
        new THREE.Vector3(Math.cos(a) * d, r * 0.35, Math.sin(a) * d),
        q,
        new THREE.Vector3(r, r * 0.7, r * (0.7 + rand() * 0.6)),
      ),
    );
  }
  rocks.instanceMatrix.needsUpdate = true;
  rocks.castShadow = true;
  rocks.receiveShadow = true;
  root.add(rocks);

  const scrubMat = track(
    new THREE.MeshStandardMaterial({ color: 0x8a8f5f, roughness: 1, flatShading: true }),
  );
  const SCRUB = 60;
  const scrub = new THREE.InstancedMesh(
    track(new THREE.ConeGeometry(1, 1, 4, 1)),
    scrubMat,
    SCRUB,
  );
  for (let i = 0; i < SCRUB; i++) {
    const a = rand() * Math.PI * 2;
    const d = KEEP_OUT + 0.2 + Math.pow(rand(), 0.7) * 9;
    const w = 0.06 + rand() * 0.13;
    e.set(0, rand() * Math.PI, 0);
    q.setFromEuler(e);
    scrub.setMatrixAt(
      i,
      m.compose(
        new THREE.Vector3(Math.cos(a) * d, 0, Math.sin(a) * d),
        q,
        new THREE.Vector3(w, w * (1.4 + rand()), w),
      ),
    );
  }
  scrub.instanceMatrix.needsUpdate = true;
  scrub.castShadow = true;
  root.add(scrub);

  /* ---------------------------------------------------------------- *
   * Megastructures
   * ---------------------------------------------------------------- */

  /*
   * The city opts out of fog and is tinted toward the horizon by hand.
   * Under fog tuned for the ground — which has to reach full haze by thirty
   * metres so the sand meets the sky without a visible rim — buildings at
   * twenty-five metres came out 90% hazed and vanished into flat plates.
   * Doing the aerial perspective explicitly keeps them readable silhouettes
   * at whatever distance suits the composition.
   */
  const CITY_BASE = new THREE.Color(0x6b6b7a);
  const CITY_DARK_BASE = new THREE.Color(0x4e4e5c);
  const cityMat = track(
    new THREE.MeshStandardMaterial({
      color: CITY_BASE.clone(),
      roughness: 0.82,
      flatShading: true,
      fog: false,
    }),
  );
  const cityDarkMat = track(
    new THREE.MeshStandardMaterial({
      color: CITY_DARK_BASE.clone(),
      roughness: 0.85,
      flatShading: true,
      fog: false,
    }),
  );

  /** Window positions accumulated across every building, lit as one mesh. */
  const windowSpots: THREE.Matrix4[] = [];

  function addWindows(
    origin: THREE.Vector3,
    faceAngle: number,
    width: number,
    height: number,
    cols: number,
    rows: number,
  ): void {
    for (let c = 0; c < cols; c++) {
      for (let r = 0; r < rows; r++) {
        // A third of the grid stays dark. A fully lit facade looks like a
        // texture; gaps are what make it look inhabited.
        if (rand() < 0.34) continue;
        const x = (c / (cols - 1 || 1) - 0.5) * width;
        const y = ((r + 0.6) / rows) * height;
        e.set(0, faceAngle, 0);
        q.setFromEuler(e);
        const p = new THREE.Vector3(
          origin.x + Math.cos(faceAngle) * x,
          origin.y + y,
          origin.z - Math.sin(faceAngle) * x,
        );
        windowSpots.push(
          new THREE.Matrix4().compose(
            p,
            q,
            new THREE.Vector3(width / cols * 0.44, height / rows * 0.3, 1),
          ),
        );
      }
    }
  }

  function slab(
    angle: number,
    distance: number,
    w: number,
    h: number,
    d: number,
    mat: THREE.Material,
  ): THREE.Vector3 {
    const geo = track(new THREE.BoxGeometry(w, h, d));
    const mesh = new THREE.Mesh(geo, mat);
    const p = new THREE.Vector3(Math.cos(angle) * distance, h / 2, Math.sin(angle) * distance);
    mesh.position.copy(p);
    mesh.rotation.y = -angle;
    root.add(mesh);
    // Front face of the slab, pushed just proud so windows never z-fight.
    const faceAngle = -angle;
    const front = new THREE.Vector3(
      p.x - Math.cos(angle) * (d / 2 + 0.02),
      0,
      p.z - Math.sin(angle) * (d / 2 + 0.02),
    );
    addWindows(front, faceAngle, w * 0.82, h * 0.9, Math.max(2, Math.round(w * 1.6)), Math.max(3, Math.round(h * 2.1)));
    return p;
  }

  /*
   * Spread right around the compass. Clustered into one quadrant — which is
   * where the first pass put them — the idle orbit spent most of its time
   * facing empty desert, and the city read as a single prop rather than a
   * skyline.
   */

  // The tower — tallest thing on the skyline, and the beacon carrier.
  const towerAngle = 1.15;
  slab(towerAngle, 27, 2.4, 8.6, 2.4, cityMat);
  slab(towerAngle + 0.06, 27.5, 1.5, 6.2, 1.5, cityDarkMat);

  // A long horizontal mass — the arts complex.
  slab(2.1, 30, 9.5, 2.4, 3, cityDarkMat);
  slab(2.35, 28, 4.5, 3.4, 2.6, cityMat);

  // A second tower group opposite, so the far side is not bare.
  slab(3.55, 26, 1.9, 6.8, 1.9, cityMat);
  slab(3.68, 27, 3.2, 3.6, 2.4, cityDarkMat);

  // Low blocks filling the two remaining quadrants.
  slab(0.65, 25, 5.5, 1.9, 3, cityDarkMat);
  slab(5.05, 29, 6.5, 2.6, 3.2, cityMat);
  slab(6.05, 26.5, 3.6, 4.2, 2.4, cityDarkMat);

  // Terraced ziggurat, stepping back as it rises.
  for (let i = 0; i < 4; i++) {
    slab(4.35, 24 + i * 0.5, 6 - i * 1.3, 1.2 + i * 1.15, 5 - i * 1.1, i % 2 ? cityMat : cityDarkMat);
  }

  // A cluster of spires, thin enough to read as filigree against the sky.
  for (let i = 0; i < 6; i++) {
    const a = 5.35 + (i - 3) * 0.055;
    const h = 3 + rand() * 4.2;
    const spire = new THREE.Mesh(track(new THREE.CylinderGeometry(0.1, 0.28, h, 6)), cityMat);
    spire.position.set(Math.cos(a) * 31, h / 2, Math.sin(a) * 31);
    root.add(spire);
  }

  // Paired conservatory domes — the same building as the pavilion's, scaled
  // to the horizon so the two ends of the scale fiction rhyme.
  for (let i = 0; i < 2; i++) {
    const a = 3.15 + i * 0.12;
    const r = 1.9 - i * 0.55;
    const domeGeo = track(new THREE.SphereGeometry(r, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2));
    const d = new THREE.Mesh(domeGeo, cityMat);
    d.position.set(Math.cos(a) * 22, 0, Math.sin(a) * 22);
    root.add(d);
    const wires = new THREE.LineSegments(
      track(new THREE.EdgesGeometry(domeGeo, 1)),
      track(new THREE.LineBasicMaterial({ color: 0x8ea0b8, transparent: true, opacity: 0.5 })),
    );
    wires.position.copy(d.position);
    root.add(wires);
  }

  // Arena ring, seen edge-on.
  const arena = new THREE.Mesh(
    track(new THREE.TorusGeometry(2.6, 0.62, 6, 18)),
    cityDarkMat,
  );
  arena.position.set(Math.cos(0.15) * 20, 0.5, Math.sin(0.15) * 20);
  arena.rotation.x = Math.PI / 2;
  root.add(arena);

  /* ---------------------------------------------------------------- *
   * Sun
   *
   * Placed along the same vector as the scene's key light so the shadows on
   * the plot fall away from it. It is a flat disc facing the origin rather
   * than a billboard: at forty metres out and with the camera never further
   * than five from the origin, the error is under four degrees and no one
   * has ever seen it.
   * ---------------------------------------------------------------- */

  const WHITE = new THREE.Color(0xffffff);
  /*
   * Same bearing as the scene's key light, but sat low rather than following
   * its elevation. The key light is up at sixty degrees, where a sun disc is
   * off the top of the frame in every shot the player will ever take — and a
   * sunset that never touches the horizon is not a sunset.
   */
  const sunAzimuth = Math.atan2(1.1, 1.6);
  const sunDir = new THREE.Vector3(
    Math.cos(sunAzimuth) * 38,
    5.2,
    Math.sin(sunAzimuth) * 38,
  );
  const sunMat = track(
    new THREE.MeshBasicMaterial({ color: 0xfff2d0, toneMapped: false, fog: false }),
  );
  const sun = new THREE.Mesh(track(new THREE.CircleGeometry(1.5, 24)), sunMat);
  sun.position.copy(sunDir);
  sun.lookAt(0, 0, 0);
  sun.renderOrder = 0;
  root.add(sun);

  const glowMat = track(
    new THREE.MeshBasicMaterial({
      color: 0xffd9a0,
      map: track(makeGlowTexture()),
      transparent: true,
      opacity: 0.9,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
      fog: false,
    }),
  );
  const glow = new THREE.Mesh(track(new THREE.PlaneGeometry(19, 19)), glowMat);
  glow.position.copy(sunDir).multiplyScalar(0.995);
  glow.lookAt(0, 0, 0);
  root.add(glow);

  /* ---------------------------------------------------------------- *
   * Lights
   *
   * One InstancedMesh for every window in the city, with a per-instance
   * colour attribute. Flicker is applied to a rotating slice each frame
   * rather than to all of them: several hundred colour writes per frame is
   * a needless upload for something the eye reads as random anyway.
   * ---------------------------------------------------------------- */

  const windowGeo = track(new THREE.PlaneGeometry(1, 1));
  const windowMat = track(
    new THREE.MeshBasicMaterial({
      color: 0xffffff,
      vertexColors: false,
      toneMapped: false,
      fog: false,
    }),
  );
  const windows = new THREE.InstancedMesh(windowGeo, windowMat, windowSpots.length);
  windowSpots.forEach((mat, i) => windows.setMatrixAt(i, mat));
  windows.instanceMatrix.needsUpdate = true;
  windows.instanceColor = new THREE.InstancedBufferAttribute(
    new Float32Array(windowSpots.length * 3),
    3,
  );
  windows.frustumCulled = false;
  root.add(windows);

  const WARM = new THREE.Color(0xffca7a);
  const COOL = new THREE.Color(0xbcd8ff);
  const baseTint: THREE.Color[] = [];
  const phase = new Float32Array(windowSpots.length);
  for (let i = 0; i < windowSpots.length; i++) {
    baseTint.push(WARM.clone().lerp(COOL, rand() * 0.55));
    phase[i] = rand() * 100;
    windows.instanceColor.setXYZ(i, baseTint[i].r, baseTint[i].g, baseTint[i].b);
  }

  // Aircraft warning beacon on the tower. Slow, red, and unmistakably not a
  // window — it is the detail that says "this thing is enormous".
  const beaconMat = track(
    new THREE.MeshBasicMaterial({ color: 0xff3b30, toneMapped: false, fog: false }),
  );
  const beacon = new THREE.Mesh(track(new THREE.SphereGeometry(0.16, 6, 5)), beaconMat);
  beacon.position.set(Math.cos(towerAngle) * 27, 8.8, Math.sin(towerAngle) * 27);
  root.add(beacon);

  /* ---------------------------------------------------------------- *
   * Lifecycle
   * ---------------------------------------------------------------- */

  let cursor = 0;
  let lightLevel = 0.55;
  const tmpColor = new THREE.Color();
  const targetSky = zenith.clone();
  const targetGround = new THREE.Color(SAND);

  return {
    root,

    setPalette(topHex: number, bottomHex: number, groundHex: number, exposure: number, dt: number) {
      const k = 1 - Math.pow(0.12, dt);
      targetSky.set(topHex);
      targetGround.set(groundHex);

      // Lerped rather than set, for the same reason the scene lights are:
      // weather turns over on a four-minute boundary the player has no reason
      // to expect, and a hard cut reads as a rendering glitch.
      const beforeZ = zenith.getHex();
      const beforeH = horizonCol.getHex();
      zenith.lerp(targetSky, k);
      horizonCol.lerp(tmpColor.set(bottomHex), k);
      if (zenith.getHex() !== beforeZ || horizonCol.getHex() !== beforeH) paintSky();

      // The sun takes the horizon colour, so a sandstorm dims it to a copper
      // disc and golden hour turns it molten without any separate authoring.
      sunMat.color.copy(horizonCol).lerp(WHITE, 0.35);
      glowMat.color.copy(horizonCol);

      sandMat.color.lerp(tmpColor.copy(targetGround).lerp(SAND_BASE, 0.5), k);
      duneMat.color.copy(sandMat.color).multiplyScalar(0.9);
      cityMat.color.copy(CITY_BASE).lerp(horizonCol, 0.52);
      cityDarkMat.color.copy(CITY_DARK_BASE).lerp(horizonCol, 0.42);
      fog.color.copy(horizonCol);

      // Darker weather means the city reads brighter. This is why the lights
      // are worth having at all in a scene that is otherwise broad daylight.
      lightLevel += (THREE.MathUtils.clamp(1.35 - exposure, 0.22, 0.95) - lightLevel) * k;
    },

    update(_dt, time) {
      // Flicker a slice per frame; the whole city cycles a few times a second.
      const slice = Math.min(64, windowSpots.length);
      const color = windows.instanceColor;
      if (color) {
        for (let n = 0; n < slice; n++) {
          const i = (cursor + n) % windowSpots.length;
          const t = time * 2.1 + phase[i];
          // Mostly steady, with an occasional deep dip — a window going dark
          // for a moment reads as someone crossing a room.
          const dip = Math.sin(t * 0.37 + phase[i]) > 0.94 ? 0.25 : 1;
          const shimmer = 0.82 + Math.sin(t) * 0.1 + Math.sin(t * 3.3) * 0.06;
          const v = lightLevel * shimmer * dip;
          color.setXYZ(i, baseTint[i].r * v, baseTint[i].g * v, baseTint[i].b * v);
        }
        cursor = (cursor + slice) % windowSpots.length;
        color.needsUpdate = true;
      }

      // ~1.4 s period, hard on and hard off, like the real thing.
      const b = Math.sin(time * 4.4) > 0.72 ? 1 : 0.06;
      beaconMat.color.setRGB(1 * b, 0.23 * b, 0.19 * b);
      beacon.scale.setScalar(0.8 + b * 0.45);
    },

    dispose() {
      for (const o of owned) o.dispose();
      owned.length = 0;
    },
  };
}
